import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { fitnessProfileModalBackdropClassName } from '../FitnessProfile/shared/fitnessProfileReadingStyles';
import {
	aiTrainerHeadingClassName,
	aiTrainerLinkClassName,
	aiTrainerMutedTextClassName,
	aiTrainerPrimaryButtonClassName,
	PERSONA_BORDER
} from './aiTrainerStyles';

const knowledgeSectionCardClassName =
	'overflow-hidden rounded-xl border border-gray-200 bg-white dark:border-dark-border dark:bg-dark-card';

const knowledgeNestedItemClassName = 'rounded-lg bg-gray-50 px-3 py-2.5 dark:bg-dark-bg';

const SECTION_ICONS = {
	profile: 'fa-id-card',
	vitals: 'fa-heart-pulse',
	lifestyle: 'fa-clipboard-list',
	goals: 'fa-bullseye',
	challenges: 'fa-flag-checkered'
};

function formatShortDate(iso) {
	if (!iso) return null;

	try {
		return new Date(iso).toLocaleDateString(undefined, {
			month: 'short',
			day: 'numeric',
			year: 'numeric'
		});
	} catch (_error) {
		return null;
	}
}

function KnowledgeSection({ icon, title, children, empty, accentClassName }) {
	if (empty) return null;

	return (
		<section className={`${knowledgeSectionCardClassName} border-l-2 ${accentClassName}`}>
			<div className="flex items-center gap-2.5 bg-gray-50 px-4 py-3 dark:bg-dark-bg">
				<span
					className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-blue-100 text-blue-700 dark:bg-dark-bg dark:text-blue-400"
					aria-hidden="true"
				>
					<i className={`fa-solid ${icon} text-sm`} />
				</span>
				<h3 className={`text-sm font-semibold ${aiTrainerHeadingClassName}`}>{title}</h3>
			</div>
			<div className="px-4 py-3.5">{children}</div>
		</section>
	);
}

function DetailRow({ label, value, showWhenMissing = false, missingLabel = 'Not on file' }) {
	if ((value == null || value === '') && !showWhenMissing) return null;

	const isMissing = value == null || value === '';

	return (
		<div className="flex items-start justify-between gap-4 py-2 text-sm">
			<dt className={`shrink-0 ${aiTrainerMutedTextClassName}`}>{label}</dt>
			<dd
				className={`min-w-0 text-right leading-snug ${
					isMissing
						? `italic ${aiTrainerMutedTextClassName}`
						: `font-medium ${aiTrainerHeadingClassName}`
				}`}
			>
				{isMissing ? missingLabel : value}
			</dd>
		</div>
	);
}

function IntakeDisclaimer({ intakeComplete, intakePath, onClose }) {
	return (
		<div
			className={`mt-3 flex w-full flex-col items-center gap-2 rounded-xl px-3 py-2.5 text-center sm:flex-row sm:items-center sm:text-left ${
				intakeComplete ? 'bg-green-50 dark:bg-dark-bg' : 'bg-yellow-50 dark:bg-dark-bg'
			}`}
		>
			<span
				className={`inline-flex shrink-0 items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold ${
					intakeComplete
						? 'bg-green-100 text-green-800 dark:bg-dark-card dark:text-green-300'
						: 'bg-yellow-100 text-yellow-900 dark:bg-dark-card dark:text-yellow-200'
				}`}
			>
				<i
					className={`fa-solid ${intakeComplete ? 'fa-circle-check' : 'fa-circle-exclamation'} text-xs`}
					aria-hidden="true"
				/>
				{intakeComplete ? 'Intake complete' : 'Intake incomplete'}
			</span>
			<p
				className={`min-w-0 w-full flex-1 text-xs leading-relaxed sm:w-auto ${aiTrainerMutedTextClassName}${
					intakeComplete ? ' hidden sm:block' : ''
				}`}
			>
				{intakeComplete ? (
					'Fitness profile and questionnaire on file.'
				) : (
					<>
						Add your fitness profile and questionnaire so coaching stays accurate.
						{intakePath && (
							<>
								{' '}
								<Link to={intakePath} className={aiTrainerLinkClassName} onClick={onClose}>
									Complete intake →
								</Link>
							</>
						)}
					</>
				)}
			</p>
		</div>
	);
}

function ChallengeGroup({ label, items, showMeta = false, withDivider = false }) {
	if (!items?.length) return null;

	return (
		<div className={withDivider ? 'mt-4 pt-4' : undefined}>
			<p className={`mb-2 text-xs font-semibold uppercase tracking-wide ${aiTrainerMutedTextClassName}`}>
				{label}
			</p>
			<ul className="space-y-2.5">
				{items.map((item, index) => (
					<li
						key={`${item.title}-${index}`}
						className={knowledgeNestedItemClassName}
					>
						<p className={`text-sm font-medium leading-snug ${aiTrainerHeadingClassName}`}>{item.title}</p>
						{showMeta && (item.status || item.urgency) && (
							<p className={`mt-0.5 text-xs ${aiTrainerMutedTextClassName}`}>
								{[item.status, item.urgency].filter(Boolean).join(' · ')}
							</p>
						)}
					</li>
				))}
			</ul>
		</div>
	);
}

export default function AiTrainerCoachKnowledgeSheet({
	open,
	coachKnowledge,
	fitnessProfileId,
	personality = 'accountability_partner',
	loading = false,
	onClose
}) {
	const modalRef = useRef(null);
	const accentClassName = PERSONA_BORDER[personality] || PERSONA_BORDER.accountability_partner;

	useEffect(() => {
		if (!open) return undefined;

		function handleKeyDown(event) {
			if (event.key === 'Escape') onClose();
		}

		window.addEventListener('keydown', handleKeyDown);
		return () => window.removeEventListener('keydown', handleKeyDown);
	}, [open, onClose]);

	useEffect(() => {
		function handleClickOutside(event) {
			if (modalRef.current && !modalRef.current.contains(event.target)) {
				onClose();
			}
		}

		if (open) {
			document.addEventListener('mousedown', handleClickOutside);
		} else {
			document.removeEventListener('mousedown', handleClickOutside);
		}

		return () => document.removeEventListener('mousedown', handleClickOutside);
	}, [open, onClose]);

	if (!open) return null;

	const intakeComplete = Boolean(coachKnowledge?.intake_complete);
	const demographics = coachKnowledge?.demographics;
	const vitals = coachKnowledge?.vitals;
	const lifestyle = coachKnowledge?.lifestyle;
	const goals = coachKnowledge?.active_goals || [];
	const challenges = coachKnowledge?.challenges || {};
	const hasDemographics = Boolean(demographics?.age || demographics?.height);
	const showProfileSection = Boolean(coachKnowledge?.profile_name || hasDemographics || !intakeComplete);
	const hasVitals =
		vitals?.latest_weight_lbs != null ||
		vitals?.blood_pressure ||
		vitals?.weigh_in_due_today;
	const lifestyleMissing = lifestyle?.status === 'questionnaire_not_on_file';
	const hasLifestyleData =
		lifestyle &&
		!lifestyleMissing &&
		Object.values(lifestyle).some((value) => value != null && value !== '');
	const showLifestyleSection = lifestyleMissing || hasLifestyleData;
	const hasGoals = goals.length > 0;
	const hasChallenges =
		challenges.active?.length ||
		challenges.pending_acceptance?.length ||
		challenges.grace_window?.length ||
		challenges.recent_history?.length;
	const profilePath = fitnessProfileId ? `/fitness-profiles/${fitnessProfileId}` : null;
	const intakePath = fitnessProfileId ? `/fitness-profiles/${fitnessProfileId}/complete-intake` : null;

	return (
		<div className={fitnessProfileModalBackdropClassName}>
			<div
				ref={modalRef}
				className="relative flex w-full min-w-0 max-w-lg flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-xl dark:border-dark-border dark:bg-dark-card dark:text-dark-text"
				style={{ maxHeight: '90vh' }}
				role="dialog"
				aria-modal="true"
				aria-labelledby="coach-knowledge-title"
			>
				<div className={`relative shrink-0 border-b border-gray-200 bg-white px-4 pb-4 pt-5 pr-12 sm:px-6 sm:pt-6 sm:pr-14 dark:border-dark-border dark:bg-dark-card`}>
					<div className="flex items-start gap-3">
						<span
							className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-blue-50 text-blue-600 dark:bg-dark-bg dark:text-blue-400 sm:h-10 sm:w-10"
							aria-hidden="true"
						>
							<i className="fa-solid fa-circle-info text-sm sm:text-base" />
						</span>
						<div className="min-w-0 flex-1">
							<h2 id="coach-knowledge-title" className={`text-lg font-bold leading-tight sm:text-xl ${aiTrainerHeadingClassName}`}>
								What my coach knows
							</h2>
							<p className={`mt-1 text-xs leading-snug sm:mt-1.5 sm:text-sm sm:leading-relaxed ${aiTrainerMutedTextClassName}`}>
								<span className="sm:hidden">Profile data your coach uses.</span>
								<span className="hidden sm:inline">
									Profile data to inform your coach's responses.
								</span>
							</p>
						</div>
					</div>
					<button
						type="button"
						onClick={onClose}
						className="absolute right-2 top-4 rounded-full p-2 text-gray-500 transition hover:bg-gray-100 hover:text-gray-800 sm:right-3 sm:top-5 dark:text-dark-text-secondary dark:hover:bg-dark-bg dark:hover:text-dark-text"
						aria-label="Close"
					>
						<svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
							<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
						</svg>
					</button>
				</div>

				<div className="min-h-0 flex-1 overflow-y-auto overscroll-contain bg-gray-50 px-6 py-5 dark:bg-dark-bg">
					{loading && !coachKnowledge && (
						<p className={`text-sm ${aiTrainerMutedTextClassName}`}>Refreshing profile data…</p>
					)}

					{coachKnowledge && (
						<div className="space-y-4">
							<KnowledgeSection
								icon={SECTION_ICONS.profile}
								title="Fitness profile"
								empty={!showProfileSection}
								accentClassName={accentClassName}
							>
								<dl>
									<DetailRow label="Name" value={coachKnowledge.profile_name} showWhenMissing />
									<DetailRow
										label="Age"
										value={demographics?.age != null ? `${demographics.age}` : null}
										showWhenMissing={!intakeComplete}
									/>
									<DetailRow
										label="Height"
										value={demographics?.height}
										showWhenMissing={!intakeComplete}
									/>
								</dl>
								<IntakeDisclaimer
									intakeComplete={intakeComplete}
									intakePath={intakePath}
									onClose={onClose}
								/>
							</KnowledgeSection>

							<KnowledgeSection
								icon={SECTION_ICONS.vitals}
								title="Vitals"
								empty={!hasVitals}
								accentClassName={accentClassName}
							>
								<dl>
									<DetailRow
										label="Latest weight"
										value={
											vitals?.latest_weight_lbs != null
												? `${vitals.latest_weight_lbs} lbs${
														vitals.weight_recorded_at
															? ` · ${formatShortDate(vitals.weight_recorded_at)}`
															: ''
													}`
												: null
										}
									/>
									<DetailRow
										label="Blood pressure"
										value={
											vitals?.blood_pressure
												? `${vitals.blood_pressure.systolic}/${vitals.blood_pressure.diastolic}`
												: null
										}
									/>
								</dl>
								{vitals?.weigh_in_due_today && (
									<p className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-blue-100 px-2.5 py-1 text-xs font-semibold text-blue-800 dark:bg-dark-bg dark:text-blue-300">
										<i className="fa-solid fa-scale-balanced text-xs" aria-hidden="true" />
										Weigh-in due today
									</p>
								)}
							</KnowledgeSection>

							<KnowledgeSection
								icon={SECTION_ICONS.lifestyle}
								title="Lifestyle"
								empty={!showLifestyleSection}
								accentClassName={accentClassName}
							>
								{lifestyleMissing ? (
									<p className={`text-sm leading-relaxed ${aiTrainerMutedTextClassName}`}>
										No fitness questionnaire on file yet.
										{intakePath && (
											<>
												{' '}
												<Link to={intakePath} className={aiTrainerLinkClassName} onClick={onClose}>
													Add questionnaire →
												</Link>
											</>
										)}
									</p>
								) : (
									<dl>
										<DetailRow label="Diet" value={lifestyle?.diet_pattern} />
										<DetailRow label="Exercise frequency" value={lifestyle?.exercise_frequency} />
										<DetailRow label="Exercise intensity" value={lifestyle?.exercise_intensity} />
										<DetailRow
											label="Sleep"
											value={
												lifestyle?.sleep_hours != null ? `${lifestyle.sleep_hours} hrs/night` : null
											}
										/>
										<DetailRow label="Equipment" value={lifestyle?.equipment_access} />
										{lifestyle?.diet_notes && (
											<div className="py-2">
												<p className={`text-xs ${aiTrainerMutedTextClassName}`}>Diet notes</p>
												<p className={`mt-1 text-sm leading-relaxed ${aiTrainerHeadingClassName}`}>
													{lifestyle.diet_notes}
												</p>
											</div>
										)}
										{lifestyle?.habit_notes && (
											<div className="py-2">
												<p className={`text-xs ${aiTrainerMutedTextClassName}`}>Habit notes</p>
												<p className={`mt-1 text-sm leading-relaxed ${aiTrainerHeadingClassName}`}>
													{lifestyle.habit_notes}
												</p>
											</div>
										)}
										<DetailRow
											label="Questionnaire updated"
											value={formatShortDate(lifestyle?.submitted_at)}
										/>
									</dl>
								)}
							</KnowledgeSection>

							<KnowledgeSection
								icon={SECTION_ICONS.goals}
								title="Active goals"
								empty={!hasGoals}
								accentClassName={accentClassName}
							>
								<ul className="space-y-2.5">
									{goals.map((goal, index) => (
										<li
											key={`${goal.outcome}-${index}`}
											className={knowledgeNestedItemClassName}
										>
											<p className={`text-sm font-medium leading-snug ${aiTrainerHeadingClassName}`}>
												{goal.outcome}
											</p>
											{(goal.target_metric || goal.target_date || goal.progress_percent != null) && (
												<p className={`mt-1 text-xs ${aiTrainerMutedTextClassName}`}>
													{[
														goal.target_metric,
														goal.target_date ? `by ${formatShortDate(goal.target_date)}` : null,
														goal.progress_percent != null ? `${goal.progress_percent}% progress` : null
													]
														.filter(Boolean)
														.join(' · ')}
												</p>
											)}
										</li>
									))}
								</ul>
							</KnowledgeSection>

							<KnowledgeSection
								icon={SECTION_ICONS.challenges}
								title="Challenges"
								empty={!hasChallenges}
								accentClassName={accentClassName}
							>
								<ChallengeGroup label="Active" items={challenges.active} showMeta />
								<ChallengeGroup
									label="Pending acceptance"
									items={challenges.pending_acceptance}
									showMeta
									withDivider={Boolean(challenges.active?.length)}
								/>
								<ChallengeGroup
									label="Awaiting your result"
									items={challenges.grace_window}
									showMeta
									withDivider={Boolean(
										challenges.active?.length || challenges.pending_acceptance?.length
									)}
								/>
								{challenges.recent_history?.length > 0 && (
									<div
										className={
											challenges.active?.length ||
											challenges.pending_acceptance?.length ||
											challenges.grace_window?.length
												? 'mt-4 pt-4'
												: undefined
										}
									>
										<p className={`mb-2 text-xs font-semibold uppercase tracking-wide ${aiTrainerMutedTextClassName}`}>
											Recent history
										</p>
										<ul className="space-y-2.5">
											{challenges.recent_history.map((entry, index) => (
												<li
													key={`${entry.title}-${index}`}
													className={knowledgeNestedItemClassName}
												>
													<p className={`text-sm font-medium ${aiTrainerHeadingClassName}`}>{entry.title}</p>
													<p className={`mt-0.5 text-xs ${aiTrainerMutedTextClassName}`}>
														{[entry.outcome || entry.status, formatShortDate(entry.ended_on)]
															.filter(Boolean)
															.join(' · ')}
													</p>
												</li>
											))}
										</ul>
									</div>
								)}
							</KnowledgeSection>
						</div>
					)}
				</div>

				<div className="flex shrink-0 flex-col gap-3 border-t border-gray-200 bg-white px-6 py-4 dark:border-dark-border dark:bg-dark-card sm:flex-row sm:items-end sm:justify-between">
					<p className={`min-w-0 text-xs leading-relaxed ${aiTrainerMutedTextClassName}`}>
						Updates when you log weight, finish challenges, or refresh your questionnaire.
						{profilePath && (
							<>
								{' '}
								<Link to={profilePath} className={aiTrainerLinkClassName} onClick={onClose}>
									Edit profile →
								</Link>
							</>
						)}
					</p>
					<button type="button" onClick={onClose} className={`${aiTrainerPrimaryButtonClassName} shrink-0`}>
						Done
					</button>
				</div>
			</div>
		</div>
	);
}
